import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getToken, removeToken } from './auth';
import toast from 'react-hot-toast';
import {
  ApiResponse,
  PaginatedResponse,
  Category,
  Product,
  DesignTemplate,
  CustomerDesign,
  Cart,
  CartTotals,
  Order,
  Customer,
  Address,
  ReprintPaymentContext,
  ReprintVerifyPayload,
} from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 30000,
});

// Request interceptor: attach token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Track whether we've already shown the session-expired toast for the current
// 401 burst — prevents N parallel requests producing N stacked toasts.
let sessionExpiredHandled = false;

// Response interceptor: handle errors
api.interceptors.response.use(
  (response) => {
    // Reset the 401 dedupe latch on any successful response — a fresh login is
    // implicit when requests are succeeding again.
    sessionExpiredHandled = false;
    return response;
  },
  (error: AxiosError<ApiResponse<null>>) => {
    // Capture the X-Request-Id from the failed response (when present) so it
    // can be appended to user-facing toasts for support correlation.
    const requestId =
      (error.response?.headers?.['x-request-id'] as string | undefined) ||
      (error.response?.headers?.['X-Request-Id'] as string | undefined) ||
      undefined;
    const reqSuffix = requestId ? ` (req: ${String(requestId).slice(0, 12)})` : '';

    if (error.response) {
      const { status, data } = error.response;

      if (status === 401) {
        removeToken();
        if (typeof window !== 'undefined' && !sessionExpiredHandled) {
          sessionExpiredHandled = true;
          // Only nudge once and only for non-auth pages — calling code on the
          // login page itself shows its own inline error already.
          const path = window.location.pathname || '/';
          const isAuthPage = path.startsWith('/login') || path.startsWith('/(auth)') || path.startsWith('/auth');
          if (!isAuthPage) {
            toast.error('Your session expired. Please sign in again.');
            const returnUrl = encodeURIComponent(path + (window.location.search || ''));
            window.location.assign(`/login?returnUrl=${returnUrl}`);
          }
        }
        return Promise.reject(error);
      } else if (status === 403) {
        toast.error(`Access denied. You do not have permission to perform this action.${reqSuffix}`);
      } else if (status === 422) {
        // Validation errors - let the caller handle them
        return Promise.reject(error);
      } else if (status === 404) {
        // Not found - let the caller handle
        return Promise.reject(error);
      } else if (status === 429) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const retryAfter = (data as any)?.retry_after;
        const msg = retryAfter
          ? `Too many requests. Please wait ${retryAfter} seconds.`
          : 'Too many requests. Please wait a moment and try again.';
        toast.error(`${msg}${reqSuffix}`);
        return Promise.reject(error);
      } else if (status >= 500) {
        toast.error(`Something went wrong. Please try again.${reqSuffix}`);
      }
    } else if (error.request) {
      // No response. Two very different causes that used to share one message:
      // a genuine connectivity failure, and axios aborting a slow upload once
      // its timeout elapsed. Design saves carry canvas_json plus two base64
      // preview PNGs, so a few MB over a mobile connection routinely exceeded
      // the old 30s ceiling and surfaced as a bogus "network error".
      // The caller (designStore.save) renders its own, more specific toast for
      // saves, so stay quiet there rather than stacking two conflicting ones.
      const cfg = error.config as (typeof error.config & { _suppressNetworkToast?: boolean }) | undefined;
      if (!cfg?._suppressNetworkToast) {
        toast.error(
          error.code === 'ECONNABORTED'
            ? 'The request timed out. Please check your connection and try again.'
            : 'Network error. Please check your connection.',
        );
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// ===================== RESPONSE HELPERS =====================
// Our API returns responses in 3 patterns:
//   1. Single resource: { product: {...} } or { order: {...} }
//   2. List: { categories: [...] } or { addresses: [...] }
//   3. Paginated: { data: [...], current_page, last_page, ... }
//   4. Mutation: { message: "...", resource: {...} }
//
// These helpers extract data safely regardless of wrapper key.

/** Extract a single resource from API response (handles { product }, { order }, { data }, etc.) */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractResource<T>(resData: any, ...keys: string[]): T {
  for (const key of keys) {
    if (resData?.[key] !== undefined) return resData[key];
  }
  return resData?.data ?? resData;
}

/** Extract an array from API response (handles { categories }, { products }, { data }, etc.) */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractList<T>(resData: any, ...keys: string[]): T[] {
  for (const key of keys) {
    if (Array.isArray(resData?.[key])) return resData[key];
  }
  if (Array.isArray(resData?.data)) return resData.data;
  if (Array.isArray(resData)) return resData;
  return [];
}

/** Extract pagination info from API response */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractPagination(resData: any): { data: any[]; lastPage: number; currentPage: number; total: number } {
  return {
    data: resData?.data || [],
    lastPage: resData?.meta?.last_page || resData?.last_page || 1,
    currentPage: resData?.meta?.current_page || resData?.current_page || 1,
    total: resData?.meta?.total || resData?.total || 0,
  };
}

// ===================== AUTH =====================
export const authApi = {
  sendOtp: (phone: string) =>
    api.post<ApiResponse<{ message: string; session_id: string }>>('/auth/send-otp', { phone }),

  verifyOtp: (phone: string, otp: string, sessionId: string) =>
    api.post<ApiResponse<{ token: string; customer: Customer }>>('/auth/verify-otp', { phone, otp, session_id: sessionId }),

  me: () =>
    api.get<ApiResponse<Customer>>('/auth/me'),

  logout: () =>
    api.post<ApiResponse<null>>('/auth/logout'),

  // Backend route is PUT /api/v1/profile (not /auth/profile).
  updateProfile: (data: { name?: string; email?: string }) =>
    api.put<ApiResponse<Customer>>('/profile', data),
};

// ===================== CATEGORIES =====================
export const categoryApi = {
  getAll: () =>
    api.get<ApiResponse<Category[]>>('/categories'),

  getBySlug: (slug: string) =>
    api.get<ApiResponse<Category>>(`/categories/${slug}`),

  getProducts: (slug: string, params?: { page?: number; per_page?: number }) =>
    api.get<PaginatedResponse<Product>>(`/categories/${slug}/products`, { params }),
};

// ===================== PRODUCTS =====================
export const productApi = {
  getByCategory: (categorySlug: string, params?: {
    sub_category?: string;
    sort?: string;
    page?: number;
    per_page?: number;
    // Variant-option filters: { paper_weight: '400gsm,350gsm', lamination: 'matte' }.
    // Flattened to opt[group]=csv query params the backend understands.
    opt?: Record<string, string>;
  }) => {
    const { opt, ...rest } = params || {};
    const flat: Record<string, string | number> = { ...(rest as Record<string, string | number>) };
    if (opt) {
      Object.entries(opt).forEach(([group, csv]) => {
        if (csv) flat[`opt[${group}]`] = csv;
      });
    }
    return api.get<PaginatedResponse<Product>>(`/categories/${categorySlug}/products`, { params: flat });
  },

  getAll: (params?: {
    category?: string;
    sub_category?: string;
    search?: string;
    sort?: string;
    page?: number;
    per_page?: number;
    featured?: boolean;
  }) =>
    // Redirect to correct endpoint if category specified
    params?.category
      ? api.get<PaginatedResponse<Product>>(`/categories/${params.category}/products`, { params: { ...params, category: undefined } })
      : api.get<PaginatedResponse<Product>>('/products/search', { params }),

  getBySlug: (slug: string) =>
    api.get<ApiResponse<Product>>(`/products/${slug}`),

  getFeatured: () =>
    api.get<ApiResponse<Product[]>>('/products/featured'),

  search: (query: string) =>
    api.get<ApiResponse<Product[]>>('/products/search', { params: { q: query } }),
};

// ===================== DESIGNS =====================
export const designApi = {
  getTemplates: (productSlug: string, params?: { tag?: string }) =>
    api.get<ApiResponse<DesignTemplate[]>>(`/products/${productSlug}/templates`, { params }),

  // The list endpoint intentionally omits canvas_json (it can be 50-500KB per template).
  // Fetch the full template — including canvas_json — on demand when the user clicks one.
  getTemplate: (id: number) =>
    api.get<ApiResponse<DesignTemplate>>(`/templates/${id}`),

  createDesign: (data: {
    product_id: number;
    template_id?: number;
    name: string;
    variant_selections?: Record<string, string>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Canvas JSON (Konva) has dynamic structure
    canvas_json?: any;
  }) =>
    api.post<ApiResponse<CustomerDesign>>('/designs', data),

  getDesign: (id: number) =>
    api.get<ApiResponse<CustomerDesign>>(`/designs/${id}`),

  /**
   * "Upload your own design" — attach a print-ready file (JPG/PNG/PDF/PSD/TIFF)
   * to a product instead of using the editor. Returns a finalized CustomerDesign
   * whose id can be added straight to the cart.
   */
  uploadArtwork: (productId: number, file: File) => {
    const fd = new FormData();
    fd.append('product_id', String(productId));
    fd.append('file', file);
    return api.post<ApiResponse<CustomerDesign>>('/designs/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  updateDesign: (id: number, data: {
    name?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Canvas JSON (Konva) has dynamic structure
    canvas_json?: any;
    preview_image?: string;
    preview_image_back?: string | null;
    status?: string;
    expected_version_id?: number | null;
    create_version?: boolean;
    version_notes?: string | null;
  }) =>
    // A save is the heaviest request the app makes: canvas_json (which embeds
    // uploaded artwork as base64) plus two base64 preview PNGs. The global 30s
    // ceiling aborted those mid-upload on ordinary mobile connections, and an
    // aborted request has no response — so it surfaced as "network error" when
    // the connection was fine and the design was simply still uploading.
    // Suppress the interceptor's generic toast; designStore.save classifies the
    // failure (timeout vs offline vs too large) and shows a useful message.
    api.put<ApiResponse<CustomerDesign>>(`/designs/${id}`, data, {
      timeout: 180000,
      _suppressNetworkToast: true,
    } as Parameters<typeof api.put>[2]),

  // ---- Versioning ----
  listVersions: (id: number) =>
    api.get<ApiResponse<{
      versions: { data: Array<{ id: number; version_number: number; notes: string | null; created_at: string }> };
      current_version_id: number | null;
    }>>(`/designs/${id}/versions`),

  createVersion: (id: number, data: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    canvas_json: any;
    parent_version_id?: number | null;
    notes?: string | null;
  }) =>
    api.post<ApiResponse<{ version: { id: number; version_number: number } }>>(`/designs/${id}/versions`, data),

  restoreVersion: (id: number, versionId: number) =>
    api.post<ApiResponse<{ design: CustomerDesign; version: { id: number; version_number: number } }>>(
      `/designs/${id}/versions/${versionId}/restore`,
      {},
    ),

  // ---- Share links ----
  createShareLink: (id: number, data: { expires_in_hours?: number; allow_edit?: boolean }) =>
    api.post<ApiResponse<{ url: string; token: string; mode: 'view' | 'edit'; expires_at: string }>>(
      `/designs/${id}/share-links`,
      data,
    ),

  // Public — uses the full signed URL returned by createShareLink.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchShared: (signedUrl: string) => api.get<any>(signedUrl, { baseURL: '' }),

  getMyDesigns: () =>
    api.get<ApiResponse<CustomerDesign[]>>('/designs'),

  deleteDesign: (id: number) =>
    api.delete<ApiResponse<null>>(`/designs/${id}`),

  /**
   * Upload a high-res print-source PNG for one side of a design. Multipart so
   * the 30MB+ raw bytes don't suffer the 33% base64 inflation that JSON would
   * impose. Backend validates pixel dimensions vs (trim + bleed) * DPI before
   * persisting under design_print_sources/{id}/{side}.png.
   */
  uploadPrintSource: (
    designId: number,
    params: {
      side: 'front' | 'back';
      blob: Blob;
      pageSizeMm: { w: number; h: number };
      bleedMm: number;
      pixelRatio?: number;
    },
  ) => {
    const fd = new FormData();
    fd.append('side', params.side);
    fd.append('image', params.blob, `${params.side}.png`);
    fd.append('page_size_mm', JSON.stringify(params.pageSizeMm));
    fd.append('bleed_mm', String(params.bleedMm));
    if (params.pixelRatio !== undefined) fd.append('pixel_ratio', String(params.pixelRatio));
    return api.post<ApiResponse<{ side: string; path: string; size_bytes: number; sha256: string }>>(
      `/designs/${designId}/print-source`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },
};

// ===================== AI DESIGN =====================
// Generates a starter canvas layout from a customer-supplied brief. Returns
// an unsaved canvas_json — the editor applies it to the stage and the
// customer hits Save (or tweaks first) to persist via designApi.updateDesign.
export const aiDesignApi = {
  // Optional `signal` lets the caller abort an in-flight generation when the
  // customer hits Generate again before the previous request resolves. Without
  // it we'd race two payloads onto the canvas in arrival order, which is
  // confusing — the second click should always supersede the first.
  generate: (
    data: { design_id: number; prompt: string; canvas_width?: number; canvas_height?: number },
    config?: { signal?: AbortSignal },
  ) =>
    api.post<ApiResponse<{
      message: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Canvas JSON (Konva) has dynamic structure
      canvas_json: any;
      // 3 distinct takes for the same brief; canvas_json mirrors variations[0].
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Canvas JSON (Konva) has dynamic structure
      variations?: any[];
      provider: string;
    }>>('/ai/design/generate', data, config),
};

// ===================== CART =====================
export const cartApi = {
  get: () =>
    api.get<ApiResponse<{ cart: Cart; totals: CartTotals }>>('/cart'),

  addItem: (data: {
    product_id: number;
    design_id?: number | null;
    variant_selections?: Record<string, string>;
    quantity: number;
  }) =>
    api.post<ApiResponse<{ cart: Cart; totals: CartTotals }>>('/cart/items', data),

  updateItem: (itemId: number, data: { quantity: number }) =>
    api.put<ApiResponse<{ cart: Cart; totals: CartTotals }>>(`/cart/items/${itemId}`, data),

  removeItem: (itemId: number) =>
    api.delete<ApiResponse<{ cart: Cart; totals: CartTotals }>>(`/cart/items/${itemId}`),

  applyCoupon: (code: string) =>
    api.post<ApiResponse<{ cart: Cart; totals: CartTotals }>>('/cart/apply-coupon', { code }),

  removeCoupon: () =>
    api.delete<ApiResponse<{ cart: Cart; totals: CartTotals }>>('/cart/remove-coupon'),

  toggleLoyalty: () =>
    api.post<ApiResponse<{ cart: Cart; totals: CartTotals }>>('/cart/toggle-loyalty'),
};

// ===================== ORDERS =====================
export const orderApi = {
  // Place order hits the checkout controller (not /orders). The /orders collection is read-only
  // (index/show/cancel/tracking/invoice) — order *creation* lives under /checkout/place-order and
  // returns the Razorpay order payload the frontend needs to open the payment modal.
  create: (data: { address_id: number; payment_method?: 'razorpay' | 'cod' }) =>
    api.post<ApiResponse<{ order: Order; razorpay: { razorpay_order_id: string; razorpay_key: string; amount: number; currency: string; name: string; description: string; prefill: { name: string; email: string; contact: string } } | null }>>('/checkout/place-order', {
      payment_method: 'razorpay',
      ...data,
    }),

  verifyPayment: (data: {
    order_id: number;
    razorpay_payment_id: string;
    razorpay_order_id: string;
    razorpay_signature: string;
  }) =>
    api.post<ApiResponse<Order>>('/payments/verify', data),

  // Restart payment on a pending order whose Razorpay attempt never completed
  // (dismissed modal, declined card, gateway outage at checkout). Returns the
  // same { order, razorpay } payload shape as create().
  pay: (orderNumber: string) =>
    api.post<ApiResponse<{ order: Order; razorpay: { razorpay_order_id: string; razorpay_key: string; amount: number; currency: string; name: string; description: string; prefill: { name: string; email: string; contact: string } } }>>(`/orders/${orderNumber}/pay`),

  getAll: (params?: { page?: number }) =>
    api.get<PaginatedResponse<Order>>('/orders', { params }),

  getById: (id: number) =>
    api.get<ApiResponse<Order>>(`/orders/${id}`),

  // Customer-facing detail by order_number (the canonical identifier in URLs and WhatsApp links).
  show: (orderNumber: string) =>
    api.get<ApiResponse<Order>>(`/orders/${orderNumber}`),

  invoice: (orderNumber: string) =>
    api.get<ApiResponse<{ invoice_number: string; download_url: string }>>(
      `/orders/${orderNumber}/invoice`,
    ),
};

// ===================== ADDRESSES =====================
export const addressApi = {
  getAll: () =>
    api.get<ApiResponse<Address[]>>('/addresses'),

  create: (data: Omit<Address, 'id'>) =>
    api.post<ApiResponse<Address>>('/addresses', data),

  update: (id: number, data: Partial<Address>) =>
    api.put<ApiResponse<Address>>(`/addresses/${id}`, data),

  delete: (id: number) =>
    api.delete<ApiResponse<null>>(`/addresses/${id}`),

  // Backend route is PUT (not PATCH) — using patch here returned 405.
  setDefault: (id: number) =>
    api.put<ApiResponse<Address>>(`/addresses/${id}/default`),
};

// ===================== BANNERS =====================
export const bannerApi = {
  getAll: () =>
    api.get<ApiResponse<{ id: number; image: string; title: string; subtitle: string; link: string }[]>>('/banners'),
};

// ===================== SITE CONFIG =====================
// Public feature flags the admin controls from the panel (e.g. whether the
// "Become a Service Provider" option shows on the website).
export interface BusinessCardsConfig {
  promo_enabled: boolean;
  promo_text: string;
  hero_title: string;
  hero_subtitle: string;
  cta_templates: string;
  cta_blank: string;
  cta_upload: string;
  meta_title: string;
  meta_description: string;
}

export interface SiteConfig {
  sp_registration_visible: boolean;
  sp_register_url: string;
  /** Admin: Settings → General. Empty string = hide that contact option. */
  support_phone?: string;
  support_email?: string;
  support_whatsapp?: string;
  business_cards?: BusinessCardsConfig;
  promo_bar?: {
    enabled?: boolean;
    text?: string;
    code?: string;
    cta_label?: string;
    cta_url?: string;
  };
}

export const siteConfigApi = {
  get: () => api.get<SiteConfig>('/site-config'),
};

// ===================== HOMEPAGE (admin-managed sections) =====================
export interface HomeHeroSlide {
  title: string;
  highlight: string;
  subtitle: string;
  price_text: string;
  cta_label: string;
  cta_url: string;
  image_url: string | null;
  /** Optional full-bleed background video (MP4/WebM); plays muted & looped. */
  video_url?: string | null;
  bg_from: string;
  bg_to: string;
}

export interface HomeHeroCard {
  title: string;
  subtitle: string;
  cta_label: string;
  cta_url: string;
  image_url: string | null;
  theme: 'orange' | 'blue' | 'green' | string;
}

export interface HomeFlashItem {
  deal_price: string;
  original_price: string;
  badge: string;
  product: { id: number; name: string; slug: string; image_url: string | null };
}

export interface HomeMobileBanner {
  label: string;
  url: string;
  image_url: string | null;
}

export interface HomePayload {
  hero_slides: { items?: HomeHeroSlide[] };
  hero_cards: { items?: HomeHeroCard[] };
  /** Phone-only 900×300 banner slider; when non-empty it replaces the hero on mobile. */
  mobile_banners?: { items?: HomeMobileBanner[] };
  categories_row?: { enabled?: boolean; title?: string };
  best_sellers?: { enabled?: boolean; title?: string };
  how_it_works?: { enabled?: boolean; title?: string; items?: { title: string; subtitle: string }[] };
  flash_sale: { enabled?: boolean; title?: string; ends_at?: string; ends_at_iso?: string | null; view_all_url?: string; items?: HomeFlashItem[] };
  promo_banners: { items?: { title: string; subtitle: string; cta_label: string; cta_url: string; theme: string; image_url?: string | null }[] };
  corporate: { enabled?: boolean; title?: string; items?: { label: string; url: string; image_url: string | null }[] };
  trust_bar: { items?: { icon: string; title: string; subtitle: string }[] };
  testimonials: { enabled?: boolean; title?: string; items?: { name: string; role: string; quote: string; stars: string; avatar_url: string | null }[] };
  brands: { enabled?: boolean; title?: string; items?: { name: string; logo_url: string | null }[] };
  newsletter: { enabled?: boolean; title?: string; subtitle?: string; button_label?: string; note?: string };
}

export const homeApi = {
  get: () => api.get<HomePayload>('/homepage'),
  subscribe: (email: string) => api.post<{ ok: boolean; message: string }>('/newsletter', { email }),
};

// ===================== CMS (pages & menus) =====================
export interface CmsPageData {
  title: string;
  slug: string;
  subtitle: string | null;
  body_html: string | null;
  meta_title: string | null;
  meta_description: string | null;
  updated_at: string | null;
}

export interface MenuLinkData {
  label: string;
  url: string;
  new_tab: boolean;
}

export interface SiteMenus {
  menus: Partial<Record<'header' | 'footer_help' | 'footer_company' | 'footer_policies', MenuLinkData[]>>;
}

export const cmsApi = {
  getPage: (slug: string) => api.get<CmsPageData>(`/pages/${encodeURIComponent(slug)}`),
  getMenus: () => api.get<SiteMenus>('/menus'),
};

// ===================== WISHLIST =====================
export const wishlistApi = {
  getAll: () =>
    api.get<ApiResponse<Product[]>>('/wishlist'),

  toggle: (productId: number) =>
    api.post<ApiResponse<null>>('/wishlist', { product_id: productId }),

  remove: (productId: number) =>
    api.delete<ApiResponse<null>>(`/wishlist/${productId}`),
};

// ===================== NOTIFICATIONS =====================
export interface Notification {
  id: number;
  type: string;
  title: string;
  body: string | null;
  data: Record<string, string> | null;
  read_at: string | null;
  created_at: string;
}

export const notificationApi = {
  getAll: () =>
    api.get<ApiResponse<Notification[]>>('/notifications'),

  markRead: (id: number) =>
    api.patch<ApiResponse<null>>(`/notifications/${id}/read`),

  markAllRead: () =>
    api.post<ApiResponse<null>>('/notifications/read-all'),
};

// ===================== REPRINT PAYMENTS =====================
// Public, token-gated. Used by the /pay/reprint/[orderNumber] page that
// customers land on from the WhatsApp/email link backend sends after a
// reprint is approved in charge mode. No auth header — the SHA1 token
// is the access credential.
export const reprintPaymentApi = {
  fetch: (orderNumber: string, token: string) =>
    api.get<ReprintPaymentContext>(`/reprint-payments/${orderNumber}`, {
      params: { token },
    }),

  verify: (orderNumber: string, payload: ReprintVerifyPayload) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- backend returns the freshly-updated Order; shape lives in the order module and is not strictly typed here
    api.post<{ message: string; order: any }>(
      `/reprint-payments/${orderNumber}/verify`,
      payload
    ),
};

// ===================== REVIEWS =====================
export const reviewApi = {
  // Public product reviews are keyed by SLUG (GET /products/{slug}/reviews).
  getForProduct: (slug: string, params?: { page?: number }) =>
    api.get<PaginatedResponse<{
      id: number;
      customer_name: string;
      rating: number;
      comment: string;
      created_at: string;
    }>>(`/products/${slug}/reviews`, { params }),

  // Submitting a review is order-scoped: the backend (POST /reviews) requires
  // order_id + product_id and only accepts reviews on delivered orders.
  create: (data: { order_id: number; product_id: number; rating: number; comment?: string }) =>
    api.post<ApiResponse<{ review: unknown }>>('/reviews', data),
};

export const supportApi = {
  // Public support-ticket submission. A logged-in customer's token (if present)
  // links the ticket to their account server-side.
  createTicket: (data: { name: string; email?: string; phone?: string; subject: string; message: string }) =>
    api.post<{ message: string; ticket_number: string }>('/support/tickets', data),
};

export const fontApi = {
  // Admin-uploaded custom fonts available to the design editor.
  list: () =>
    api.get<{ fonts: { name: string; family: string; format: string; url: string }[] }>('/fonts'),
};

export const industryApi = {
  // Admin-defined custom industry groups for the template Industry filter.
  list: () =>
    api.get<{ industries: { name: string; keywords: string[] }[] }>('/industries'),
};
